// client/src/pages/PapabileDuJourAdmin.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import '../styles/PapabileDuJourAdmin.css';

// Composants SVG inline
const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const IconDelete = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconEye = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const PapabileDuJourAdmin = () => {
  // État pour la liste des papabiles du jour
  const [papabilesJour, setPapabilesJour] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // État pour le filtre et la recherche
  const [searchTerm, setSearchTerm] = useState('');
  
  // État pour l'édition
  const [editMode, setEditMode] = useState(false);
  const [currentPapabile, setCurrentPapabile] = useState(null);
  
  // État pour la liste des cardinaux papabili disponibles
  const [cardinaux, setCardinaux] = useState([]);
  
  // État pour les erreurs d'image
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';
  
  // Chargement initial des données
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Récupérer la liste des papabiles du jour
        const papabilesResponse = await axios.get('/api/papabile-jour');
        setPapabilesJour(papabilesResponse.data);
        
        // Récupérer la liste des cardinaux papabili
        const cardinauxResponse = await axios.get('/api/cardinaux/search?papabile=true');
        setCardinaux(cardinauxResponse.data.cardinaux);
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filtrer les papabiles en fonction du terme de recherche
  const filteredPapabiles = papabilesJour.filter(papabile => {
    if (!searchTerm) return true;
    
    const searchTermLower = searchTerm.toLowerCase();
    
    // Recherche sur le nom complet, prénom, nom, pays et fonctions du cardinal
    return (
      papabile.cardinalId.nomComplet?.toLowerCase().includes(searchTermLower) ||
      papabile.cardinalId.nom?.toLowerCase().includes(searchTermLower) ||
      papabile.cardinalId.prenom?.toLowerCase().includes(searchTermLower) ||
      papabile.cardinalId.paysService?.toLowerCase().includes(searchTermLower) ||
      papabile.cardinalId.paysNaissance?.toLowerCase().includes(searchTermLower) ||
      papabile.cardinalId.continent?.toLowerCase().includes(searchTermLower) ||
      papabile.cardinalId.titre?.toLowerCase().includes(searchTermLower) ||
      papabile.citation?.toLowerCase().includes(searchTermLower) ||
      papabile.commentaire?.toLowerCase().includes(searchTermLower)
    );
  });
  
  // Trier par date d'affichage (décroissant)
  const sortedPapabiles = [...filteredPapabiles].sort((a, b) => 
    new Date(b.dateAffichage) - new Date(a.dateAffichage)
  );
  
  // Créer un nouveau papabile du jour
  const createPapabile = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    setCurrentPapabile({
      cardinalId: '',
      dateAffichage: tomorrow.toISOString().split('T')[0],
      citation: '',
      commentaire: '',
      actif: true
    });
    
    setEditMode(true);
  };
  
  // Éditer un papabile existant
  const editPapabile = (papabile) => {
    setCurrentPapabile({
      id: papabile._id,
      cardinalId: papabile.cardinalId._id,
      dateAffichage: new Date(papabile.dateAffichage).toISOString().split('T')[0],
      citation: papabile.citation || '',
      commentaire: papabile.commentaire || '',
      actif: papabile.actif
    });
    
    setEditMode(true);
  };
  
  // Supprimer un papabile du jour
  const deletePapabile = async (id, nom) => {
    // Demander confirmation
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le papabile du jour pour ${nom} ?`)) {
      return;
    }
    
    try {
      await axios.delete(`/api/papabile-jour/${id}`);
      
      // Mettre à jour la liste
      setPapabilesJour(papabilesJour.filter(p => p._id !== id));
      
      // Afficher un message de succès
      setSuccessMessage('Papabile du jour supprimé avec succès');
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Erreur lors de la suppression du papabile du jour');
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Annuler l'édition
  const cancelEdit = () => {
    setCurrentPapabile(null);
    setEditMode(false);
  };
  
  // Gérer les changements dans le formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentPapabile({
      ...currentPapabile,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Trouver l'URL de l'image du cardinal sélectionné
  const getSelectedCardinalImage = () => {
    if (!currentPapabile || !currentPapabile.cardinalId) return defaultImage;
    
    const cardinal = cardinaux.find(c => c._id === currentPapabile.cardinalId);
    return cardinal && cardinal.photo ? getApiImageUrl(cardinal.photo, cardinal._id) : defaultImage;
  };
  
  // Sauvegarder un papabile (nouveau ou modifié)
  const savePapabile = async (e) => {
    e.preventDefault();
    
    // Validation des données
    if (!currentPapabile.cardinalId) {
      setError('Veuillez sélectionner un cardinal');
      return;
    }
    
    if (!currentPapabile.dateAffichage) {
      setError('Veuillez sélectionner une date d\'affichage');
      return;
    }
    
    try {
      // Format des données pour l'API
      const papabileData = {
        cardinalId: currentPapabile.cardinalId,
        dateAffichage: currentPapabile.dateAffichage,
        citation: currentPapabile.citation,
        commentaire: currentPapabile.commentaire,
        actif: currentPapabile.actif !== undefined ? currentPapabile.actif : true
      };
      
      let updatedPapabile;
      
      if (currentPapabile.id) {
        // Mise à jour
        const response = await axios.put(`/api/papabile-jour/${currentPapabile.id}`, papabileData);
        updatedPapabile = response.data;
        
        // Mettre à jour la liste
        setPapabilesJour(papabilesJour.map(p => 
          p._id === currentPapabile.id ? updatedPapabile : p
        ));
        
        setSuccessMessage('Papabile du jour mis à jour avec succès');
      } else {
        // Création
        const response = await axios.post('/api/papabile-jour', papabileData);
        updatedPapabile = response.data;
        
        // Ajouter à la liste
        setPapabilesJour([...papabilesJour, updatedPapabile]);
        
        setSuccessMessage('Nouveau papabile du jour créé avec succès');
      }
      
      // Réinitialiser le formulaire
      setCurrentPapabile(null);
      setEditMode(false);
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
      
      // Masquer le message d'erreur après 5 secondes
      setTimeout(() => {
        setError(null);
      }, 5000);
    }
  };
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, cardinalId) => {
    if (!originalUrl || imageErrors[cardinalId]) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (cardinalId) => {
    console.log(`Erreur de chargement d'image pour cardinal ${cardinalId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [cardinalId]: true
    }));
  };
  
  return (
    <div className="papabile-jour-admin-page">
      <h1>Administration des Papabili du Jour</h1>
      
      {/* Affichage des messages */}
      {error && <div className="message error">{error}</div>}
      {successMessage && <div className="message success">{successMessage}</div>}
      
      {/* Mode édition */}
      {editMode ? (
        <div className="edit-papabile-section">
          <h2>{currentPapabile.id ? 'Modifier le papabile du jour' : 'Créer un nouveau papabile du jour'}</h2>
          
          <form onSubmit={savePapabile} className="papabile-form">
            <div className="form-columns">
              <div className="form-column">
                <div className="form-group">
                  <label>Cardinal</label>
                  <select 
                    name="cardinalId" 
                    value={currentPapabile.cardinalId} 
                    onChange={handleInputChange} 
                    required
                  >
                    <option value="">Sélectionner un cardinal papabile</option>
                    {cardinaux.map(cardinal => (
                      <option key={cardinal._id} value={cardinal._id}>
                        {cardinal.nomComplet} ({cardinal.paysService})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Date d'affichage</label>
                  <input 
                    type="date" 
                    name="dateAffichage" 
                    value={currentPapabile.dateAffichage} 
                    onChange={handleInputChange} 
                    required
                  />
                </div>
                
                <div className="form-group checkbox">
                  <label>
                    <input 
                      type="checkbox" 
                      name="actif" 
                      checked={currentPapabile.actif} 
                      onChange={handleInputChange} 
                    />
                    Actif
                  </label>
                </div>
                
                <div className="form-group">
                  <label>Citation</label>
                  <textarea 
                    name="citation" 
                    value={currentPapabile.citation} 
                    onChange={handleInputChange} 
                    rows="3"
                    placeholder="Citation optionnelle du cardinal"
                  />
                </div>
                
                <div className="form-group">
                  <label>Commentaire</label>
                  <textarea 
                    name="commentaire" 
                    value={currentPapabile.commentaire} 
                    onChange={handleInputChange} 
                    rows="5"
                    placeholder="Commentaire ou présentation du cardinal"
                  />
                </div>
              </div>
              
              <div className="form-column preview-column">
                <h3>Aperçu</h3>
                
                <div className="cardinal-preview">
                  {currentPapabile.cardinalId ? (
                    <>
                      <div className="preview-photo">
                        <img 
                          src={getSelectedCardinalImage()} 
                          alt="Aperçu du cardinal" 
                          onError={() => {/* Utiliser l'image par défaut */}}
                        />
                      </div>
                      <div className="preview-info">
                        {currentPapabile.cardinalId && (
                          <p className="preview-name">
                            {cardinaux.find(c => c._id === currentPapabile.cardinalId)?.nomComplet || 'Cardinal sélectionné'}
                          </p>
                        )}
                        <p className="preview-date">
                          {currentPapabile.dateAffichage ? format(new Date(currentPapabile.dateAffichage), 'EEEE d MMMM yyyy', { locale: fr }) : 'Date non spécifiée'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="no-cardinal-selected">
                      Sélectionnez un cardinal pour voir l'aperçu
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-button">
                {currentPapabile.id ? 'Mettre à jour' : 'Créer'}
              </button>
              <button type="button" className="cancel-button" onClick={cancelEdit}>
                Annuler
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
          {/* Interface de gestion des papabiles du jour */}
          <div className="admin-controls">
            <div className="search-bar">
              <IconSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Rechercher un cardinal..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <button className="create-button" onClick={createPapabile}>
              + Nouveau Papabile du Jour
            </button>
          </div>
          
          {/* Liste des papabiles du jour */}
          <div className="papabiles-list">
            {loading ? (
              <div className="loading">Chargement des papabiles du jour...</div>
            ) : sortedPapabiles.length === 0 ? (
              <div className="no-results">
                {searchTerm ? 
                  "Aucun papabile ne correspond aux critères de recherche" : 
                  "Aucun papabile du jour n'est disponible"}
              </div>
            ) : (
              <table className="papabiles-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Cardinal</th>
                    <th>Photo</th>
                    <th>Citation</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPapabiles.map(papabile => (
                    <tr key={papabile._id} className={papabile.actif ? '' : 'inactive-row'}>
                      <td>{format(new Date(papabile.dateAffichage), 'dd/MM/yyyy')}</td>
                      <td>{papabile.cardinalId.nomComplet}</td>
                      <td>
                        <img 
                          src={getApiImageUrl(papabile.cardinalId.photo, papabile.cardinalId._id)} 
                          alt={papabile.cardinalId.nomComplet} 
                          className="papabile-mini-photo" 
                          onError={() => handleImageError(papabile.cardinalId._id)}
                        />
                      </td>
                      <td>
                        {papabile.citation ? 
                          (papabile.citation.length > 50 ? 
                            `${papabile.citation.substring(0, 50)}...` : 
                            papabile.citation) : 
                          '—'}
                      </td>
                      <td>
                        <span className={`status-badge ${papabile.actif ? 'active' : 'inactive'}`}>
                          {papabile.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <a 
                            href={`/papabile-du-jour/${papabile.slug}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="view-button"
                            title="Voir sur le site"
                          >
                            <IconEye />
                          </a>
                          
                          <button 
                            className="edit-button" 
                            onClick={() => editPapabile(papabile)}
                            title="Modifier"
                          >
                            <IconEdit />
                          </button>
                          
                          <button 
                            className="delete-button" 
                            onClick={() => deletePapabile(papabile._id, papabile.cardinalId.nomComplet)}
                            title="Supprimer"
                          >
                            <IconDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PapabileDuJourAdmin;